export type GigPlatformRole = 'client' | 'freelancer' | 'unknown';

export interface RoleAnalysis {
  role: GigPlatformRole;
  confidence: number;
  reasoning: string;
  consumerProtectionApplies: boolean;
  disclaimer?: string;
}

export class GigRoleDetector {
  private static readonly CLIENT_KEYWORDS = [
    'order', 'hired', 'gig', 'task', 'refund', "didn't deliver",
    'freelancer', 'ordered', 'paid for', 'not completed', 'unsatisfied',
    'milestone', 'project not done', 'want refund'
  ];

  private static readonly FREELANCER_KEYWORDS = [
    'my client', 'delivered', 'not paid', 'completed', 'work done',
    'payment delayed', 'client refused', 'finished project', 'submitted work',
    'client dispute', 'payment withheld'
  ];

  static detectRole(issue: string, merchantName: string): RoleAnalysis {
    const searchText = `${issue} ${merchantName}`.toLowerCase();
    
    let clientScore = 0;
    let freelancerScore = 0;
    
    // Count keyword matches
    this.CLIENT_KEYWORDS.forEach(keyword => {
      if (searchText.includes(keyword.toLowerCase())) {
        clientScore++;
      }
    });
    
    this.FREELANCER_KEYWORDS.forEach(keyword => {
      if (searchText.includes(keyword.toLowerCase())) {
        freelancerScore++;
      }
    });
    
    // Determine role based on scores
    let role: GigPlatformRole = 'unknown';
    let confidence = 0;
    let reasoning = '';
    
    if (clientScore > freelancerScore && clientScore > 0) {
      role = 'client';
      confidence = Math.min(clientScore / 3, 1); // Normalize to 0-1
      reasoning = `Client keywords detected: seeking refund or disputing work delivery`;
    } else if (freelancerScore > clientScore && freelancerScore > 0) {
      role = 'freelancer';
      confidence = Math.min(freelancerScore / 3, 1);
      reasoning = `Freelancer keywords detected: discussing completed work or payment issues`;
    } else {
      role = 'unknown';
      confidence = 0;
      reasoning = 'Unable to determine if user is client or freelancer';
    }
    
    const consumerProtectionApplies = role === 'client';
    const disclaimer = role === 'freelancer' 
      ? 'Consumer protection may apply only to clients. Freelancers are subject to contract and platform terms.'
      : undefined;
    
    return {
      role,
      confidence,
      reasoning,
      consumerProtectionApplies,
      disclaimer
    };
  }
}